/**
 * src/screens/schedules/SchedulesList.tsx — the /schedules list.
 *
 * Moved out of `./_bundle.tsx` (which now re-exports this module) as the
 * sanctioned per-surface decomposition: the bundle carries a blanket
 * `no-explicit-any` / react-refresh disable and four unrelated screens, so a
 * co-located test could not target the real component. Behaviour of the
 * pre-existing surface (create / unassign / delete / row navigation) is
 * unchanged by the move.
 *
 * NMW-486 — "Reiniciar programación".
 * ---------------------------------------------------------------------------
 * An on-air vod2live channel plays the schedule AS IT STOOD at go-live: the
 * playlist is baked into the pod's argv at create time and nothing re-reads it.
 * The only way to apply an edit was to leave this view, stop the channel and
 * publish again. This row action does the whole stop → real-teardown-wait →
 * recreate cycle through `api.schedules.restartProgramming`.
 *
 * Honest-state contract for this surface (the load-bearing part):
 *
 *   - The action is only OFFERED for a schedule bound to a channel. On a
 *     channel that is not on air it renders DISABLED with the real reason —
 *     never a decorative affordance, and never silently absent mid-incident
 *     (NMW-457).
 *   - The success toast promises INITIATION, never playback. There is no
 *     readinessProbe on the deployment and the container waits up to 90s for
 *     its first segment, so no reachable signal proves "al aire con la nueva
 *     programación". Do NOT upgrade this copy to "En vivo" / "Listo".
 *   - Progress is a reactive subscription (`api.channelState.getRestartingChannels`,
 *     ONE flat query for the whole list — never one per row), never a poll and
 *     never client-only state. A reload, a second operator or a crashed action
 *     all read the same durable truth.
 *   - A failed restart is shown as failed and only clears when the operator
 *     dismisses it (`acknowledgeRestartFailure`). Because the backend keeps
 *     `desiredState: "live"` through every failure path, the auto-relaunch
 *     supervisor can legitimately bring the channel back up outside the op —
 *     so the pill reads the channel's REAL status alongside the op state and
 *     degrades to "volvió al aire" instead of asserting the channel is down.
 */
/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useCallback, useMemo } from 'react';
import { useQuery } from 'convex/react';
import {
  Icon, Btn, Pill, Panel, PageHead, AccessGate, EmptyState, SkeletonList,
  Modal, ConfirmModal, Dropdown, Field, Input, Select,
} from '../../components/primitives';
import { ago } from '../../lib/format';
import { navigate } from '../../lib/nav';
import { api } from '@convex/api';
import { useMutationWithToast as useHonestMutation } from '../../hooks/useMutationWithToast';
import { useChannelList } from '../../hooks/useChannelList';
import { useCurrentMember } from '../../hooks/useCurrentMember';
import { can } from '../../lib/permissions';
import { ScheduleHealthBadge, HEALTH_BADGE_LIMIT } from './ScheduleHealthBadge';

/** Row shape of `api.channelState.getRestartingChannels` (one flat subscription). */
export interface RestartOpRow {
  channelId: string;
  /** The channel's REAL status, so a `failed` op over a recovered channel reads honestly. */
  backendStatus: string;
  /** Absent on a row returned only for its restartable status (no op). */
  phase?: 'awaiting_teardown' | 'starting' | 'failed' | string;
  /** Non-terminal phase past its lease — the reapers have resumed. */
  expired: boolean;
  requestedAt?: number;
  deadlineAt?: number;
  /** Closed enum — never string-sniff the raw error for control flow. */
  failureReason?: string;
  error?: string;
  completedAt?: number;
}

/** Closed `failureReason` → Spanish motive. The raw detail goes in `title`. */
const FAILURE_MOTIVE: Record<string, string> = {
  teardown_timeout: 'el despliegue anterior no terminó de apagarse',
  start_skipped: 'ya había otro arranque en curso',
  start_error: 'no se pudo crear el nuevo despliegue',
  lease_expired: 'el reinicio se quedó sin respuesta',
};

/** Non-terminal phase → what the backend is actually doing right now. */
const PHASE_DETAIL: Record<string, string> = {
  awaiting_teardown: 'Esperando a que se apague el despliegue anterior…',
  starting: 'Creando el nuevo despliegue con la programación actual…',
};

export type RestartUiState = 'none' | 'restarting' | 'stalled' | 'failed' | 'recovered';

/**
 * Backend `RESTARTABLE_STATUSES`, mirrored. `error` is the PRIMARY mid-incident
 * case: the channel is wedged, `desiredState` is still `live`, and restarting is
 * exactly the remedy — «Publicar ahora» is not.
 */
const RESTARTABLE_STATUSES = new Set(['running', 'ad_break', 'error']);

/**
 * Op row + the channel's live status → the single state the row paints.
 * Pure and exported so the honest-state matrix is directly testable.
 */
export function deriveRestartUiState(
  op: RestartOpRow | undefined,
  channelOnAir: boolean,
): RestartUiState {
  if (!op) return 'none';
  if (op.phase === 'failed') return channelOnAir ? 'recovered' : 'failed';
  if (op.phase === 'awaiting_teardown' || op.phase === 'starting') {
    return op.expired ? 'stalled' : 'restarting';
  }
  return 'none';
}

export function SchedulesList() {

  // Issue #13 — "Nueva programación" previously routed to the placeholder
  // `/schedules/sc_new/edit`, which the wired editor renders as an honest
  // "aún no disponible" stub. Wire it to the real `api.schedules.createSchedule`
  // mutation: collect a name in a small inline modal, create the container,
  // then navigate to its real editor route. Routed through the honest-error
  // wrapper (FR-013 H1) — argKeys-only logging, fail-loud on error.
  const createScheduleM = useHonestMutation(api.schedules.createSchedule, {
    logLabel: 'schedules_createSchedule',
    successMessage: 'Programación creada.',
    errorMessagePrefix: 'No se pudo crear la programación',
  });
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  // Delete — the backend `api.schedules.deleteSchedule` already exists (cascades
  // the schedule's items; refuses an `active` schedule). It just had no UI. A
  // typed-free danger confirm gates it; the assigned-schedule case is kept out
  // of the menu entirely, so the backend guard is a backstop, not the first line.
  const deleteScheduleM = useHonestMutation(api.schedules.deleteSchedule, {
    logLabel: 'schedules_deleteSchedule',
    successMessage: 'Programación eliminada.',
    errorMessagePrefix: 'No se pudo eliminar la programación',
  });
  // NMW-335: unassign frees a schedule from its channel so it can be deleted
  // (delete is guarded by channel-association). Backend `unassignFromChannel`
  // existed but had no UI — this wires it onto the row.
  const unassignScheduleM = useHonestMutation(api.schedules.unassignFromChannel, {
    logLabel: 'schedules_unassignFromChannel',
    successMessage: 'Programación desasignada del canal.',
    errorMessagePrefix: 'No se pudo desasignar la programación',
  });
  // NMW-486 — the restart. Note the success copy: the mutation ACK means the
  // stop was issued and the settle loop armed, NOT that the channel is back on
  // air. No `mutationName` is passed, so the wrapper never offers a Retry
  // button on the error toast — a restart is not retry-safe.
  const restartProgrammingM = useHonestMutation(api.schedules.restartProgramming, {
    logLabel: 'schedules_restartProgramming',
    successMessage: 'Reinicio iniciado — el canal volverá al aire en unos instantes.',
    errorMessagePrefix: 'No se pudo reiniciar la programación',
  });
  const acknowledgeRestartM = useHonestMutation(api.channelState.acknowledgeRestartFailure, {
    logLabel: 'channelState_acknowledgeRestartFailure',
    errorMessagePrefix: 'No se pudo descartar el aviso',
  });
  const [deleteTarget, setDeleteTarget] = useState<{ id: string; name: string } | null>(null);
  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    try {
      await deleteScheduleM.run({ scheduleId: deleteTarget.id as any });
    } catch {
      // The honest wrapper already surfaced the error toast (e.g. active schedule).
    } finally {
      setDeleteTarget(null);
    }
  }, [deleteTarget, deleteScheduleM]);
  // NMW-486 — fires straight from the row button, no confirmation step (owner
  // decision). The safety that remains is real rather than ceremonial: the
  // button is disabled unless the channel is genuinely restartable, the backend
  // enforces a 90s debounce and a restart lease so a double-click cannot start
  // two restarts, and a failed restart leaves `desiredState: "live"` so the
  // supervisor relaunches the channel on its own.
  const handleRestart = useCallback(async (scheduleId: string, channelId: string) => {
    try {
      await restartProgrammingM.run({
        scheduleId: scheduleId as any,
        channelId: channelId as any,
      });
    } catch {
      // The honest wrapper already surfaced the real backend refusal
      // ("Ya hay un reinicio en curso…", "El canal no está al aire…", …).
    }
  }, [restartProgrammingM]);
  // Assigning a channel at creation matters: addScheduleItemToContainer
  // rejects items on a channel-less container ("Schedule must be assigned to
  // a channel before adding items"), so a name-only schedule would dead-end
  // the moment the operator tries to add content. Picking the channel here
  // makes the new schedule immediately usable in the editor.
  const [newChannelId, setNewChannelId] = useState('');
  const handleCreate = useCallback(async () => {
    const name = newName.trim() === '' ? 'Nueva programación' : newName.trim();
    try {
      const newId = await createScheduleM.run({
        name,
        ...(newChannelId ? { channelId: newChannelId as any } : {}),
      });
      setCreateOpen(false);
      setNewName('');
      setNewChannelId('');
      if (newId) navigate('/schedules/' + newId + '/edit');
    } catch {
      // The honest wrapper already surfaced a red toast; keep the modal open.
    }
  }, [newName, newChannelId, createScheduleM]);

  // Real backend listing — sorted by updatedAt desc by the query (convex/
  // schedules.ts:34). `undefined` = loading; `[]` = genuinely empty.
  const schedules = useQuery(api.schedules.listSchedules, {}) as
    | Array<{
        _id: string;
        name: string;
        status: 'draft' | 'ready' | 'scheduled' | 'active' | 'completed' | 'archived';
        channelId?: string;
        totalDurationMs?: number;
        itemCount?: number;
        createdAt: number;
        updatedAt?: number;
      }>
    | undefined;
  // Already mounted for the "Canal" badge — it also carries the live
  // `backendStatus` (ONE batch snapshot, not N per-row fan-outs), which is
  // exactly the on-air signal the restart action needs. No new subscription.
  const channels = useChannelList();
  const channelById = useMemo(() => {
    if (channels === undefined) return null;
    const m = new Map<string, { name: string; backendStatus: string }>();
    for (const c of channels) m.set(c._id, { name: c.name, backendStatus: c.backendStatus });
    return m;
  }, [channels]);
  // NMW-486 — one flat subscription for the whole list. `undefined` = still
  // resolving: the restart item is disabled with that as its stated reason
  // rather than offered against an unknown op state.
  const restartOps = useQuery(api.channelState.getRestartingChannels, {}) as
    | RestartOpRow[]
    | undefined;
  const restartByChannel = useMemo(() => {
    const m = new Map<string, RestartOpRow>();
    for (const r of restartOps ?? []) m.set(r.channelId, r);
    return m;
  }, [restartOps]);

  // Same authority AccessGate consumes (the server `members` row plus the
  // client permission mirror) — not a second authorization path. Read once at
  // list level so a per-row AccessGate skeleton never lands inside a 38px row.
  const member = useCurrentMember();
  const canEditSchedules = member != null && can('schedule_create', member.role);
  const canRestart = member != null && can('channel_mode_set', member.role);

  const loading = schedules === undefined || channelById === null;

  const list = schedules ?? [];

  return (
    <div>
      <PageHead title="Programaciones" description="Planes de programación para tus canales."
        actions={<AccessGate perm="schedule_create"><Btn variant="solid" icon="plus" onClick={() => { setNewName(''); setCreateOpen(true); }}>Nueva programación</Btn></AccessGate>} />
      {loading ? <SkeletonList rows={6} /> : <>
      <Panel pad={0}>
        {list.map((s, i) => {
          // Assigned to a channel = in use; drives the accent + the delete guard
          // (a schedule assigned to a channel can't be deleted — unassign first).
          const isAssigned = !!s.channelId;
          const channel = s.channelId ? channelById?.get(s.channelId) : undefined;
          const channelName = s.channelId ? (channel?.name ?? '—') : 'Sin asignar';
          const durationH = s.totalDurationMs ? Math.round(s.totalDurationMs / 3_600_000) : 0;
          // The channel-header derivation, verbatim: only these two statuses are
          // "al aire". Absence from the running snapshot defaults to idle.
          const onAir = channel?.backendStatus === 'running' || channel?.backendStatus === 'ad_break';
          const op = s.channelId ? restartByChannel.get(s.channelId) : undefined;
          // Prefer the op row's own `backendStatus` when present: the running
          // snapshot drops `stopping` / `starting` channels, so mid-restart the
          // two subscriptions disagree for a tick.
          const opOnAir = op ? (op.backendStatus === 'running' || op.backendStatus === 'ad_break') : onAir;
          const uiState = deriveRestartUiState(op, opOnAir);
          const restartInFlight = uiState === 'restarting' || uiState === 'stalled';
          // Eligibility, not "on air": the backend accepts `error` too, and the
          // running snapshot flattens it to `idle`. The op row carries the real
          // `channelState.status`, so prefer it and fall back to the snapshot.
          const restartEligible = op
            ? RESTARTABLE_STATUSES.has(op.backendStatus)
            : onAir;
          // The backend refuses an empty container outright; disabling here with
          // the real reason beats sending the operator through the typed
          // REINICIAR gauntlet to reach a red toast. (`loopEnabled` has no
          // list-level source, so that refusal stays backend-only.)
          const scheduleIsEmpty = (s.itemCount ?? 0) === 0;
          // The recreated deployment is built from the CHANNEL's playlist
          // (`by_channel_time`), which is the union of every container assigned
          // to it — not just this row's. Nothing enforces one container per
          // channel, so state the channel-level consequence.
          const channelItemCount = s.channelId
            ? list.reduce((n, x) => (x.channelId === s.channelId ? n + (x.itemCount ?? 0) : n), 0)
            : (s.itemCount ?? 0);

          // Why the restart is (or is not) offered. Absent only when there is no
          // channel at all — otherwise disabled WITH the reason, because
          // mid-incident a silently missing action is worse than a stated no.
          let restartDisabledReason: string | null = null;
          if (!canRestart) {
            restartDisabledReason = 'Necesitas el permiso "channel_mode_set" para reiniciar una programación al aire.';
          } else if (restartOps === undefined) {
            restartDisabledReason = 'Comprobando el estado del canal…';
          } else if (restartInFlight) {
            restartDisabledReason = 'Ya hay un reinicio en curso para este canal.';
          } else if (!restartEligible) {
            restartDisabledReason = 'El canal no está al aire — usa «Publicar ahora» para salir al aire.';
          } else if (scheduleIsEmpty) {
            restartDisabledReason = 'Esta programación está vacía — no hay nada que reiniciar.';
          }

          const menuItems: any[] = [];
          if (canEditSchedules) {
            if (isAssigned) {
              menuItems.push({
                label: 'Desasignar',
                onClick: () => { void unassignScheduleM.run({ scheduleId: s._id as any }); },
              });
            } else {
              // Deleting an assigned schedule is refused by the backend
              // (unassign first), so the item is omitted rather than offered.
              menuItems.push({
                label: 'Eliminar',
                icon: 'trash',
                danger: true,
                onClick: () => setDeleteTarget({ id: s._id, name: s.name }),
              });
            }
          }

          return (
          <div key={s._id} onClick={() => navigate('/schedules/' + s._id + '/edit')} style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '16px 20px', cursor: 'pointer', borderBottom: i < list.length - 1 ? '1px solid var(--lightest-gray)' : 'none' }}
            onMouseEnter={e => e.currentTarget.style.background = 'var(--snow)'} onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: isAssigned ? 'var(--pale-green)' : 'var(--snow)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <Icon name="calendar" size={18} color={isAssigned ? 'var(--deep-green)' : 'var(--muted-slate)'} />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              {/* NMW-452 — at-a-glance validation state. A name is a name, a
                  status is a tag: the badge is a sibling Pill, never appended
                  to the name text. Past HEALTH_BADGE_LIMIT rows it degrades to
                  an honest "Sin verificar" instead of subscribing. */}
              <div style={{ fontWeight: 500, fontSize: 15, marginBottom: 4, display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{s.name}</span>
                <ScheduleHealthBadge scheduleId={s._id} enabled={i < HEALTH_BADGE_LIMIT} />
              </div>
              <div style={{ display: 'flex', gap: 14, fontSize: 12, color: 'var(--muted-slate)', flexWrap: 'wrap' }}>
                <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}><Icon name="tv" size={12} />{channelName}</span>
                <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}><Icon name="list" size={12} />{s.itemCount ?? 0} elementos</span>
                <span style={{ display: 'inline-flex', gap: 5, alignItems: 'center' }}><Icon name="clock" size={12} />{durationH} h</span>
              </div>
            </div>
            {/* NMW-486 — restart state, straight from the backend op row. The
                slot only occupies width when there IS an op (the NMW-452 health
                badge keeps its own fixed 104px slot on the title line and is
                untouched), and it carries `data-state` so the honest-state
                matrix is asserted structurally, not by copy. */}
            <span
              className="circa-restart-slot"
              data-testid="schedule-restart-state"
              data-state={uiState}
              onClick={(e) => e.stopPropagation()}
            >
              {/* `Pill` takes no tooltip prop, so the detail lives on a
                  wrapping span — one place, no primitive change. */}
              {uiState === 'restarting' && (
                <span title={PHASE_DETAIL[op?.phase ?? ''] ?? 'Reinicio en curso.'} style={{ display: 'inline-flex' }}>
                  <Pill tone="warn" dot>Reiniciando…</Pill>
                </span>
              )}
              {/* An expired lease owns nothing: the reapers have resumed and
                  `acknowledgeRestartFailure` accepts it, so the operator gets
                  the same explicit dismiss as a failure instead of a red pill
                  that no action can clear. */}
              {uiState === 'stalled' && (<>
                <span title="El reinicio superó su plazo; el supervisor de canales lo está recuperando." style={{ display: 'inline-flex' }}>
                  <Pill tone="bad">Reinicio sin respuesta</Pill>
                </span>
                <Btn size="sm" variant="ghost" loading={acknowledgeRestartM.loading}
                  onClick={() => { void acknowledgeRestartM.run({ channelId: s.channelId as any }); }}>Descartar</Btn>
              </>)}
              {uiState === 'failed' && (<>
                <span title={op?.error ?? 'Sin detalle del servidor.'} style={{ display: 'inline-flex' }}>
                  <Pill tone="bad">
                    {'Reinicio falló — ' + (FAILURE_MOTIVE[op?.failureReason ?? ''] ?? 'motivo desconocido')}
                  </Pill>
                </span>
                <Btn size="sm" variant="ghost" loading={acknowledgeRestartM.loading}
                  onClick={() => { void acknowledgeRestartM.run({ channelId: s.channelId as any }); }}>Descartar</Btn>
              </>)}
              {uiState === 'recovered' && (<>
                <span title={op?.error ?? 'Sin detalle del servidor.'} style={{ display: 'inline-flex' }}>
                  <Pill tone="warn">El reinicio falló, pero el canal volvió al aire</Pill>
                </span>
                <Btn size="sm" variant="ghost" loading={acknowledgeRestartM.loading}
                  onClick={() => { void acknowledgeRestartM.run({ channelId: s.channelId as any }); }}>Descartar</Btn>
              </>)}
            </span>
            <span className="t-mono" style={{ fontSize: 11, color: 'var(--muted-slate)', width: 90, textAlign: 'right' }}>{ago(s.updatedAt ?? s.createdAt)}</span>
            {/* Restart is a first-class row button, not a menu item: it is the
                whole point of coming back to this list after editing a parrilla,
                and burying the primary action behind "…" costs a click every
                time. Present-but-disabled with the real reason in `title` —
                mid-incident a silently missing action is worse than a stated no.
                stopPropagation because the row itself navigates. */}
            {isAssigned && (
              <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }} data-testid="schedule-row-restart">
                <Btn
                  size="sm"
                  variant="ghost"
                  icon="refresh"
                  loading={restartProgrammingM.loading}
                  disabled={restartDisabledReason !== null}
                  title={restartDisabledReason ?? `Reinicia ${channelName} ahora: detiene la transmisión y la recrea con la programación tal como está (${channelItemCount} elementos). El canal queda fuera del aire entre 30 s y 2 min y en YouTube se genera un video nuevo.`}
                  onClick={() => { void handleRestart(s._id, s.channelId as string); }}
                >Reiniciar</Btn>
              </span>
            )}
            {/* Remaining row actions keep the house row-menu idiom (same shape as
                the Templates card and the ProgrammeGuide row). The outer
                stopPropagation span is mandatory — Dropdown does not stop
                propagation and the row itself navigates. */}
            {menuItems.length > 0 && (
              <span onClick={(e) => e.stopPropagation()} style={{ display: 'inline-flex' }} data-testid="schedule-row-menu">
                <Dropdown align="right" items={menuItems}
                  trigger={<Btn variant="ghost" icon="moreH" size="sm" title="Acciones" />} />
              </span>
            )}
            <Icon name="chevronR" size={16} color="var(--muted-slate)" />
          </div>
          );
        })}
        {list.length === 0 && <div style={{ padding: 24 }}><EmptyState icon="calendar" title="Sin programaciones" body="Crea una programación para empezar." /></div>}
      </Panel>
      </>}
      <Modal open={createOpen} onClose={() => setCreateOpen(false)} title="Nueva programación" width={480}
        footer={<><Btn variant="ghost" onClick={() => setCreateOpen(false)}>Cancelar</Btn>
          <Btn variant="solid" onClick={handleCreate} loading={createScheduleM.loading} disabled={!newChannelId}>Crear</Btn></>}>
        <Field label="Nombre">
          <Input value={newName} onChange={setNewName} placeholder="Nueva programación" />
        </Field>
        <Field label="Canal" hint="Necesario para poder añadir contenido a la programación." required>
          <Select
            value={newChannelId}
            onChange={setNewChannelId}
            full
            options={[
              { value: '', label: 'Selecciona un canal…' },
              ...(channels ?? []).map((c: any) => ({ value: c._id, label: c.name })),
            ]}
          />
        </Field>
      </Modal>
      <ConfirmModal
        open={deleteTarget !== null}
        onClose={() => setDeleteTarget(null)}
        title="Eliminar programación"
        danger
        confirmLabel="Eliminar"
        body={<>Se eliminará <strong>{deleteTarget?.name}</strong> y todos sus elementos. Esta acción no se puede deshacer.</>}
        onConfirm={() => { void handleDelete(); }}
      />
      {/* NMW-486 — no restart confirmation by owner decision: the button acts on
          click. The consequences live in the button's own tooltip so they are
          readable BEFORE the click rather than after it. */}
    </div>
  );
}

export default SchedulesList;
